/**
 * The mastery sheet — the one student report, as a component.
 *
 * Three surfaces render it: `/reports/:spId`, where a submitted paper lands;
 * the Progress tab, where a student picks which paper to look at; and the
 * teacher's one-student view, which is deliberately the same document the child
 * is holding. It lives here rather than in any page because a second copy of a
 * fifteen-column grid is the kind of duplicate that drifts silently.
 *
 * The layout is the paper sheet a teacher already hands out: one heavy-ruled
 * block with a name strip on top, the five mastery bands down the left rail, and
 * the totals across the bottom. The bands ARE the reading order — a student
 * scans the Pink rows first because they are last, not because a chart told them
 * to — which is why the rows are sorted into rail groups rather than left in
 * serial order.
 *
 * A question the student never reached has no band — Pink means three attempts
 * and no solve — so it is listed in its own neutral group after the five.
 *
 * Fifteen columns do not fit a phone, and squeezing them would lose the sheet.
 * So the grid scrolls horizontally with the rail and the question number pinned,
 * and below 640px it becomes one card per question under the same five bands.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { BinGlyph, BinPill } from '@/components/ds';
import { BIN_LABEL, BIN_NOTE, BIN_ORDER, binRamp } from '@/lib/bins';
import type { BinColor, MasterySheetReport, MasterySheetRow } from '@cwy/config/contracts';
import {
  BandRamp,
  DS_BIN,
  FooterStat,
  gradeLabel,
  HeaderField,
  TYPE_LABEL,
} from '@/components/reports/ActionPlanKit';
import { ReportSkeleton } from '@/components/reports/ReportSkeleton';
import { HelplineDrawer } from '@/components/exam/HelplineDrawer';
import { AITutorPanel } from '@/components/exam/AITutorPanel';
import { AITutorTopicPanel } from '@/components/home/AITutorTopicPanel';

function fmt(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

/** The sheet's column order, and the width each one is allowed to take. */
const COLUMNS: { key: string; label: string; width: number; align?: 'center' }[] = [
  { key: 'question', label: 'Question', width: 92 },
  { key: 'mm', label: 'MM', width: 46, align: 'center' },
  { key: 'mo', label: 'MO', width: 46, align: 'center' },
  { key: 'time', label: 'Time', width: 92 },
  { key: 'topic', label: 'Topic', width: 150 },
  { key: 'subtopic', label: 'Sub-topic', width: 150 },
  { key: 'descriptor', label: 'Descriptor', width: 210 },
  { key: 'practise', label: 'Practise papers', width: 96, align: 'center' },
  { key: 'videos', label: 'Videos', width: 66, align: 'center' },
  { key: 'hints', label: 'Hints', width: 60, align: 'center' },
  { key: 'theory', label: 'Theory', width: 66, align: 'center' },
  { key: 'cards', label: 'Task cards', width: 82, align: 'center' },
  { key: 'error', label: 'Common error analysis', width: 230 },
  { key: 'ai', label: 'AI tutor', width: 110, align: 'center' },
];

const RAIL_WIDTH = 104;

/** The colour stops a question with no band is drawn in. */
const UNREACHED = {
  bg: 'var(--surface-card)',
  fill: 'var(--paper-sunk)',
  border: 'var(--border-strong)',
  text: 'var(--ink-3)',
  deep: 'var(--ink-2)',
};

const rampFor = (bin: BinColor | null) => (bin ? binRamp(bin) : UNREACHED);
const isMastered = (bin: BinColor | null) => bin === 'GREEN' || bin === 'PURPLE';
const QUESTION_WIDTH = COLUMNS[0].width;

/**
 * One support count.
 *
 * A zero is a dash rather than a `0` on purpose: fifteen columns of digits give
 * the eye nothing to catch, and what a student needs to spot is where the
 * support actually went.
 */
function Tally({ value, tone }: { value: number; tone: string }) {
  if (!value) {
    return (
      <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
        —
      </span>
    );
  }
  return (
    <span
      data-numeric=""
      className="inline-flex h-6 min-w-6 items-center justify-center rounded-md px-1.5 text-xs font-bold"
      style={{ background: 'var(--surface-card)', color: tone, border: `1px solid ${tone}` }}
    >
      {value}
    </span>
  );
}

export function MasterySheet({
  spId,
  viewer = 'student',
}: {
  spId: string;
  /**
   * Who is reading. It changes one thing: the tutor. The per-question tutor is
   * gated on the STUDENT's own Pink card, so a teacher reading the same sheet
   * is offered the topic tutor instead. Nothing about the sheet itself differs.
   */
  viewer?: 'student' | 'teacher';
}) {
  const [openRow, setOpenRow] = useState<MasterySheetRow | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['report', spId, 'mastery-sheet'],
    queryFn: () => apiGet<MasterySheetReport>(`/reports/${spId}/mastery-sheet`),
    refetchInterval: (query) =>
      query.state.data?.rows.some((row) => row.reviewReason) ? 5_000 : false,
  });

  /**
   * Rows regrouped into the five bands, empty bands included — the rail is fixed.
   *
   * Always Green to Pink. The teacher's copy used to flip this to lead with the
   * weakest band, which meant the sheet a teacher discussed at a PTM was not the
   * sheet the child was holding. One report, one reading order.
   */
  const bands = useMemo(() => {
    const rows = data?.rows ?? [];
    const unreached = rows.filter((r) => r.bin === null && !r.reviewReason);
    const reviewing = rows.filter((r) => r.reviewReason && r.reviewReason !== 'grading');
    const grading = rows.filter((r) => r.reviewReason === 'grading');
    return [
      ...BIN_ORDER.map(
        (
          bin,
        ): {
          key: string;
          label: string;
          note: string;
          bin: BinColor | null;
          rows: MasterySheetRow[];
        } => ({
          key: bin,
          label: BIN_LABEL[bin],
          note: BIN_NOTE[bin],
          bin,
          rows: rows.filter((r) => r.bin === bin),
        }),
      ),
      // Only when there is one: an empty "not reached" group on every finished
      // paper would read as a sixth band, and there is not one.
      ...(reviewing.length
        ? [
            {
              key: 'review',
              label: 'Teacher review',
              note: 'not graded',
              bin: null,
              rows: reviewing,
            },
          ]
        : []),
      ...(grading.length
        ? [{ key: 'grading', label: 'Grading', note: 'awaiting result', bin: null, rows: grading }]
        : []),
      ...(unreached.length
        ? [
            {
              key: 'unreached',
              label: 'Not reached',
              note: 'still to try',
              bin: null,
              rows: unreached,
            },
          ]
        : []),
    ];
  }, [data]);

  if (isLoading) return <ReportSkeleton variant="table" />;

  if (isError || !data) {
    return (
      <p className="p-6 text-center" style={{ color: 'var(--ink-3)' }}>
        This paper has no report yet — submit it first.
      </p>
    );
  }

  const { header, footer, binCounts, rows } = data;

  return (
    <>
      {rows.some((row) => row.reviewReason) && (
        <p
          role="status"
          className="mb-3 rounded-card border border-line bg-paper p-3 text-sm text-ink-soft"
        >
          Answers awaiting grading or teacher review have no grade or bucket and are excluded from
          the marks and average shown here.
        </p>
      )}
      {/* The sheet: name strip, ruled grid, totals — one bordered block. */}
      <section
        id="cwy-mastery-sheet"
        className="overflow-hidden"
        style={{
          background: 'var(--surface-card)',
          border: '1.5px solid var(--border-strong)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        {/* ── Name strip ── */}
        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3"
          style={{
            borderBottom: '1.5px solid var(--border-strong)',
            background: 'var(--paper-sunk)',
          }}
        >
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <HeaderField label="Student name" value={header.studentName} />
            <HeaderField label="Grade / Section" value={header.gradeSection} />
            <HeaderField label="Subject" value={header.subject} />
            <HeaderField label="Paper" value={header.paperTitle} />
          </div>

          <div className="flex flex-none items-center gap-3">
            <div className="text-right">
              <div
                data-numeric=""
                className="text-xl font-extrabold leading-none"
                style={{ color: 'var(--text-strong)' }}
              >
                {header.totalEarned}
                <span className="text-sm font-bold" style={{ color: 'var(--ink-3)' }}>
                  /{header.totalPossible}
                </span>
              </div>
              <div className="mt-1 w-28">
                <BandRamp counts={binCounts} total={rows.length || 1} />
              </div>
            </div>
            <span
              className="shrink-0 rounded-full px-3 py-1 text-xs font-bold"
              style={{ background: 'var(--slab)', color: 'var(--text-on-forest)' }}
            >
              Grade {gradeLabel(header.grade)} · {header.board}
            </span>
          </div>
        </div>

        {/* ── The grid (desktop) ── */}
        <div className="hidden overflow-x-auto sm:block">
          <table
            className="text-left text-fs-12.5"
            style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '100%' }}
          >
            <thead>
              <tr>
                {/* Blank corner: the rail below it is labelled by its bands. */}
                <th
                  className="sticky left-0 z-30"
                  style={{
                    width: RAIL_WIDTH,
                    minWidth: RAIL_WIDTH,
                    background: 'var(--paper-sunk)',
                    borderBottom: '1.5px solid var(--border-strong)',
                    borderRight: '1.5px solid var(--border-strong)',
                  }}
                />
                {COLUMNS.map((c, i) => (
                  <th
                    key={c.key}
                    className="px-2 py-2.5 align-bottom text-fs-10 font-bold uppercase leading-tight tracking-[0.06em]"
                    style={{
                      width: c.width,
                      minWidth: c.width,
                      color: 'var(--ink-2)',
                      background: 'var(--paper-sunk)',
                      textAlign: c.align ?? 'left',
                      borderBottom: '1.5px solid var(--border-strong)',
                      borderRight:
                        i === COLUMNS.length - 1 ? undefined : '1px solid var(--border-subtle)',
                      position: i === 0 ? 'sticky' : undefined,
                      left: i === 0 ? RAIL_WIDTH : undefined,
                      zIndex: i === 0 ? 30 : undefined,
                    }}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>

            {bands.map(({ key, label, note, bin, rows: bandRows }) => {
              const ramp = rampFor(bin);
              const span = Math.max(bandRows.length, 1);
              return (
                <tbody key={key}>
                  {(bandRows.length ? bandRows : [null]).map((row, idx) => {
                    const cell = {
                      background: ramp.bg,
                      borderBottom: '1px solid var(--border-subtle)',
                      borderRight: '1px solid var(--border-subtle)',
                    };
                    return (
                      <tr key={row?.questionId ?? `${bin}-empty`}>
                        {idx === 0 && (
                          <td
                            rowSpan={span}
                            className="sticky left-0 z-20 px-2 py-2 align-top"
                            style={{
                              width: RAIL_WIDTH,
                              minWidth: RAIL_WIDTH,
                              background: ramp.fill,
                              color: ramp.deep,
                              borderBottom: '1.5px solid var(--border-strong)',
                              borderRight: '1.5px solid var(--border-strong)',
                            }}
                          >
                            <span className="flex items-center gap-1.5">
                              {bin && (
                                <BinGlyph
                                  bin={DS_BIN[bin]}
                                  size={10}
                                  color="currentColor"
                                  title={null}
                                />
                              )}
                              <span className="text-fs-13 font-extrabold">{label}</span>
                            </span>
                            <span className="mt-0.5 block text-fs-10 font-semibold opacity-80">
                              {note}
                            </span>
                            <span
                              data-numeric=""
                              className="mt-1 block text-fs-11 font-bold opacity-90"
                            >
                              {bandRows.length} Q
                            </span>
                          </td>
                        )}

                        {row === null ? (
                          <td
                            colSpan={COLUMNS.length}
                            className="px-3 py-3 text-xs italic"
                            style={{ ...cell, color: 'var(--ink-3)' }}
                          >
                            Nothing landed in this band.
                          </td>
                        ) : (
                          <>
                            <td
                              className="sticky left-0 z-10 px-2 py-2"
                              style={{ ...cell, left: RAIL_WIDTH, width: QUESTION_WIDTH }}
                            >
                              <button
                                type="button"
                                onClick={() => setOpenRow(row)}
                                className="text-left"
                                style={{ color: 'var(--text-strong)' }}
                              >
                                <span data-numeric="" className="font-extrabold">
                                  Q{row.qNumber}
                                </span>
                                <span
                                  className="mt-0.5 block text-fs-10 font-semibold"
                                  style={{ color: 'var(--ink-3)' }}
                                >
                                  {TYPE_LABEL[row.type] ?? row.type}
                                </span>
                              </button>
                            </td>

                            <td
                              data-numeric=""
                              className="px-2 py-2 text-center font-semibold"
                              style={{ ...cell, color: 'var(--text-body)' }}
                            >
                              {row.maxMarks}
                            </td>
                            <td
                              data-numeric=""
                              className="px-2 py-2 text-center font-extrabold"
                              style={{ ...cell, color: ramp.text }}
                            >
                              {row.reviewReason ? '—' : row.marksObtained}
                            </td>
                            <td className="px-2 py-2" style={cell}>
                              <span
                                data-numeric=""
                                className="font-semibold"
                                style={{
                                  color: row.overTime ? 'var(--danger)' : 'var(--text-body)',
                                }}
                              >
                                {row.reviewReason ? '—' : fmt(row.secondsTaken)}
                              </span>
                              <span
                                data-numeric=""
                                className="block text-fs-10"
                                style={{ color: 'var(--ink-3)' }}
                              >
                                of {fmt(row.allocatedSeconds)}
                              </span>
                            </td>
                            <td
                              className="px-2 py-2"
                              style={{ ...cell, color: 'var(--text-body)' }}
                            >
                              {row.topicName}
                            </td>
                            <td
                              className="px-2 py-2"
                              style={{ ...cell, color: 'var(--text-body)' }}
                            >
                              {row.subtopicName}
                            </td>
                            <td
                              className="px-2 py-2 text-fs-11.5"
                              style={{ ...cell, color: 'var(--ink-2)' }}
                            >
                              {row.descriptor}
                            </td>

                            <td className="px-2 py-2 text-center" style={cell}>
                              <Tally value={row.practisePapers} tone={ramp.border} />
                            </td>
                            <td className="px-2 py-2 text-center" style={cell}>
                              <Tally value={row.videos} tone={ramp.border} />
                            </td>
                            <td className="px-2 py-2 text-center" style={cell}>
                              <Tally value={row.hints} tone={ramp.border} />
                            </td>
                            <td className="px-2 py-2 text-center" style={cell}>
                              <Tally value={row.theory} tone={ramp.border} />
                            </td>
                            <td className="px-2 py-2 text-center" style={cell}>
                              <Tally value={row.taskCards} tone={ramp.border} />
                            </td>

                            <td
                              className="px-2 py-2 text-fs-11.5"
                              style={{ ...cell, color: 'var(--ink-2)' }}
                            >
                              {row.reviewReason ? (
                                'Awaiting review'
                              ) : row.commonError ? (
                                row.commonError
                              ) : (
                                <span style={{ color: 'var(--ink-3)' }}>
                                  Clean — nothing to fix
                                </span>
                              )}
                            </td>

                            <td
                              className="px-2 py-2 text-center"
                              style={{ ...cell, borderRight: undefined }}
                            >
                              {bin === null ? (
                                <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
                                  —
                                </span>
                              ) : isMastered(bin) ? (
                                <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
                                  Mastered
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setOpenRow(row)}
                                  className="rounded-full px-2.5 py-1 text-fs-11 font-bold transition"
                                  style={{
                                    background: ramp.border,
                                    color: 'var(--text-on-forest)',
                                  }}
                                >
                                  Ask tutor
                                </button>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              );
            })}
          </table>
        </div>

        {/* ── The grid (phone): the same five bands, one card per question ── */}
        <div className="sm:hidden">
          {bands.map(({ key, label, note, bin, rows: bandRows }) => {
            const ramp = rampFor(bin);
            return (
              <div key={key} style={{ borderBottom: '1.5px solid var(--border-strong)' }}>
                <div
                  className="flex items-center justify-between px-3 py-1.5"
                  style={{ background: ramp.fill, color: ramp.deep }}
                >
                  <span className="flex items-center gap-1.5 text-xs font-extrabold">
                    {bin && (
                      <BinGlyph bin={DS_BIN[bin]} size={9} color="currentColor" title={null} />
                    )}
                    {label}
                    <span className="font-semibold opacity-80">· {note}</span>
                  </span>
                  <span data-numeric="" className="text-xs font-bold">
                    {bandRows.length} Q
                  </span>
                </div>

                {bandRows.length === 0 && (
                  <p className="px-3 py-2 text-xs italic" style={{ color: 'var(--ink-3)' }}>
                    Nothing landed in this band.
                  </p>
                )}

                {bandRows.map((row) => (
                  <button
                    key={row.questionId}
                    type="button"
                    onClick={() => setOpenRow(row)}
                    className="block w-full px-3 py-2.5 text-left"
                    style={{ background: ramp.bg, borderTop: '1px solid var(--border-subtle)' }}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span
                        data-numeric=""
                        className="text-sm font-extrabold"
                        style={{ color: 'var(--text-strong)' }}
                      >
                        Q{row.qNumber}
                        <span
                          className="ml-1.5 text-fs-10 font-semibold"
                          style={{ color: 'var(--ink-3)' }}
                        >
                          {TYPE_LABEL[row.type] ?? row.type}
                        </span>
                      </span>
                      <span
                        data-numeric=""
                        className="text-sm font-extrabold"
                        style={{ color: ramp.text }}
                      >
                        {row.reviewReason ? '—' : row.marksObtained}
                        <span style={{ color: 'var(--ink-3)' }}>/{row.maxMarks}</span>
                      </span>
                    </div>

                    <p
                      className="mt-0.5 text-fs-12 font-semibold"
                      style={{ color: 'var(--text-body)' }}
                    >
                      {row.topicName} · {row.subtopicName}
                    </p>
                    <p className="text-fs-11.5" style={{ color: 'var(--ink-2)' }}>
                      {row.descriptor}
                    </p>

                    <div
                      className="mt-1.5 flex flex-wrap items-center gap-2 text-fs-10 font-bold uppercase tracking-wide"
                      style={{ color: 'var(--ink-3)' }}
                    >
                      <span
                        data-numeric=""
                        style={{ color: row.overTime ? 'var(--danger)' : 'var(--ink-3)' }}
                      >
                        {row.reviewReason ? '—' : fmt(row.secondsTaken)} /{' '}
                        {fmt(row.allocatedSeconds)}
                      </span>
                      <span className="flex items-center gap-1">
                        Papers <Tally value={row.practisePapers} tone={ramp.border} />
                      </span>
                      <span className="flex items-center gap-1">
                        Videos <Tally value={row.videos} tone={ramp.border} />
                      </span>
                      <span className="flex items-center gap-1">
                        Hints <Tally value={row.hints} tone={ramp.border} />
                      </span>
                      <span className="flex items-center gap-1">
                        Theory <Tally value={row.theory} tone={ramp.border} />
                      </span>
                      <span className="flex items-center gap-1">
                        Cards <Tally value={row.taskCards} tone={ramp.border} />
                      </span>
                    </div>

                    {row.commonError && (
                      <p className="mt-1 text-fs-11.5" style={{ color: 'var(--ink-2)' }}>
                        {row.commonError}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        {/* ── Totals ── */}
        <div
          className="grid grid-cols-3 sm:grid-cols-6"
          style={{ borderTop: '1.5px solid var(--border-strong)', background: 'var(--paper-sunk)' }}
        >
          <FooterStat label="Total marks" value={`${header.totalEarned}/${header.totalPossible}`} />
          <FooterStat
            label="Speed up"
            value={String(footer.speedUp)}
            tone={footer.speedUp ? 'var(--orange-text)' : undefined}
          />
          <FooterStat label="Tips" value={String(footer.tips)} />
          <FooterStat label="Theory" value={String(footer.theory)} />
          <FooterStat
            label="Scaffold"
            value={String(footer.scaffold)}
            tone={footer.scaffold ? 'var(--pink-text)' : undefined}
          />
          <FooterStat label="AI" value={String(footer.ai)} />
        </div>

        <div
          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3"
          style={{
            borderTop: '1.5px solid var(--border-strong)',
            background: 'var(--brand-lime-soft)',
          }}
        >
          <span
            className="text-xs font-bold uppercase tracking-[0.08em]"
            style={{ color: 'var(--brand-ink)' }}
          >
            Marks waiting for you
          </span>
          <span
            data-numeric=""
            className="text-xl font-extrabold leading-none"
            style={{ color: 'var(--brand-ink)' }}
          >
            {footer.marksWaiting}
          </span>
          <span className="text-xs" style={{ color: 'var(--ink-2)' }}>
            {footer.marksWaitingNote}
          </span>
        </div>
        {/* The band says HOW a mark was earned, not only whether — which is the
            whole reason the sheet is sorted by band. Said once, in the child's
            words, with the client's name for it. */}
        <p
          className="px-4 py-2 text-fs-11"
          style={{ borderTop: '1px solid var(--border-subtle)', color: 'var(--ink-3)' }}
        >
          Look at how you got each mark — first try, after a hint, or not yet. Noticing that about
          yourself is metacognition, and it tells you what to practise next.
        </p>
      </section>

      <HelplineDrawer
        open={Boolean(openRow)}
        onClose={() => setOpenRow(null)}
        title={openRow ? `Q${openRow.qNumber} · ${openRow.topicName}` : ''}
        locked={false}
      >
        {openRow && (
          <div className="space-y-3 text-sm">
            {openRow.bin ? (
              <BinPill bin={DS_BIN[openRow.bin]}>
                {BIN_LABEL[openRow.bin]} · {BIN_NOTE[openRow.bin]}
              </BinPill>
            ) : (
              <p className="text-xs font-bold" style={{ color: 'var(--ink-3)' }}>
                {openRow.reviewReason
                  ? 'Awaiting grading or teacher review'
                  : 'Not reached in this sitting'}
              </p>
            )}

            <p style={{ color: 'var(--text-body)' }}>{openRow.questionText}</p>

            <dl className="space-y-1.5">
              <Detail label="Sub-topic" value={openRow.subtopicName} />
              <Detail label="Descriptor" value={openRow.descriptor} />
              <Detail
                label="Marks"
                value={
                  openRow.reviewReason
                    ? 'Not graded'
                    : `${openRow.marksObtained} of ${openRow.maxMarks}`
                }
              />
              <Detail
                label="Time"
                value={
                  openRow.reviewReason
                    ? 'Awaiting grading or teacher review'
                    : `${fmt(openRow.secondsTaken)} of ${fmt(openRow.allocatedSeconds)}${openRow.overTime ? ' (over)' : ''}`
                }
              />
              <Detail
                label="Support used"
                value={`${openRow.hints} hint${openRow.hints === 1 ? '' : 's'} · ${openRow.theory} theory · ${openRow.videos} video${openRow.videos === 1 ? '' : 's'}`}
              />
              <Detail label="Practice available" value={`${openRow.practisePapers} past papers`} />
              {openRow.commonError && <Detail label="Common error" value={openRow.commonError} />}
            </dl>

            {openRow.bin === null ? null : isMastered(openRow.bin) ? (
              <div
                className="rounded-xl p-3"
                style={{ background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
              >
                <p className="text-sm" style={{ color: 'var(--text-body)' }}>
                  Mastered — no tutor prompt needed here.
                </p>
              </div>
            ) : openRow.bin === 'PINK' && viewer === 'student' ? (
              // Pink is the only bin `POST /tutor/session` accepts — routes/tutor.ts
              // re-reads the CALLER's task card every turn and 403s anything else,
              // so neither a Blue row nor a teacher can be offered this panel.
              <AITutorPanel
                questionId={openRow.questionId}
                questionText={openRow.questionText}
                subtopicId={openRow.subtopicId}
              />
            ) : (
              <AITutorTopicPanel
                subtopicId={openRow.subtopicId}
                topicLabel={openRow.subtopicName}
              />
            )}

            {(openRow.bin === 'ORANGE' || openRow.bin === 'PINK') && viewer === 'student' && (
              <Link
                to={`/learn/basics/${openRow.subtopicId}`}
                className="block rounded-xl px-3 py-2.5 text-sm font-bold transition"
                style={{ background: 'var(--brand-lime-soft)', color: 'var(--brand-ink)' }}
              >
                Rebuild {openRow.subtopicName.toLowerCase()} from the basics →
              </Link>
            )}
          </div>
        )}
      </HelplineDrawer>
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt
        className="w-32 flex-none text-xs font-bold uppercase tracking-wide"
        style={{ color: 'var(--ink-3)' }}
      >
        {label}
      </dt>
      <dd className="flex-1" style={{ color: 'var(--text-body)' }}>
        {value}
      </dd>
    </div>
  );
}
